import type { ButtonProps, SelectProps } from '@cloudscape-design/components';
import { Button, DatePicker, FormField, Input, Modal, Select, SpaceBetween } from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';

import { createBatch } from '../api/batches';
import { fetchItemsWithFilter } from '../api/generic';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import type { Batch } from '../models/Batch';
import type { Recipe } from '../models/Recipe';
import { prefixCollection } from '../utils/prefix';
import { slugify } from '../utils/string';

interface CreateNewBatchModalProps {
  /**
   * Called after a batch is successfully created.
   */
  onBatchCreated?: (batch: Batch) => void;
  /**
   * Text displayed on the trigger button. Defaults to "Create batch".
   */
  buttonText?: string;
  /**
   * Optional Cloudscape button variant for the trigger button.
   */
  buttonVariant?: ButtonProps['variant'];
  /**
   * Optional icon to show on the trigger button.
   */
  buttonIconName?: ButtonProps['iconName'];
  /**
   * When true, disables the trigger button.
   */
  disabled?: boolean;
}

interface FormState {
  name: string;
  recipe: SelectProps.Option | null;
  startDate: string;
  quantity: string;
}

const formatDate = (date: Date) => date.toISOString().split('T')[0];

const createInitialFormState = (): FormState => ({
  name: '',
  recipe: null,
  startDate: formatDate(new Date()),
  quantity: '0',
});

const CreateNewBatchModal = ({
  onBatchCreated,
  buttonText = 'Create batch',
  buttonVariant = 'primary',
  buttonIconName = 'add-plus',
  disabled,
}: CreateNewBatchModalProps) => {
  const { tenant, userObject } = useAuthContext();
  const alert = useAlertContext();

  const [isVisible, setIsVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [formState, setFormState] = useState<FormState>(() => createInitialFormState());
  const [validate, setValidate] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadRecipes = async () => {
      setLoadingRecipes(true);
      try {
        const data = await fetchItemsWithFilter<Recipe>(prefixCollection('recipes', tenant), []);
        if (!cancelled) {
          setRecipes(data);
        }
      } catch (error) {
        console.error('Failed to load recipes', error);
        if (!cancelled) {
          alert.error('Failed to load recipes. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingRecipes(false);
        }
      }
    };

    loadRecipes();

    return () => {
      cancelled = true;
    };
  }, [tenant, alert]);

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      recipe: prev.recipe && recipes.some((r) => r.id === prev.recipe?.value) ? prev.recipe : null,
    }));
  }, [recipes]);

  const recipeOptions = useMemo<SelectProps.Options>(
    () =>
      recipes.map((recipe) => ({
        value: recipe.id,
        label: recipe.name,
        description: recipe.style,
      })),
    [recipes],
  );

  const selectedRecipe = useMemo(
    () => recipes.find((recipe) => recipe.id === formState.recipe?.value),
    [recipes, formState.recipe?.value],
  );

  const updateFormField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const closeModal = () => {
    setIsVisible(false);
    setValidate(false);
    setFormState(createInitialFormState());
  };

  const parseNumber = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const handleCreate = async () => {
    const { name, recipe, startDate, quantity } = formState;

    const errors = {
      name: !name.trim(),
      recipe: !recipe,
      startDate: !startDate,
      quantity: Number.isNaN(parseNumber(quantity)),
    };

    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      setValidate(true);
      return;
    }

    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) {
      setValidate(true);
      alert.error('Please provide a valid start date.');
      return;
    }

    try {
      setIsSaving(true);
      const batchToCreate: Omit<Batch, 'id' | 'batchId'> = {
        name: name.trim(),
        recipeId: recipe!.value as string,
        recipe: recipes.find((r) => r.id === recipe!.value),
        startDate: start,
        status: 'planned',
        quantity: parseNumber(quantity),
        volume: 0,
        yield: 0,
        expectedYield: 0,
        currentStepIndex: 0,
        stepStatus: 'not-started',
        createdBy: userObject?.uid,
        recipeSlug: selectedRecipe?.slug ?? slugify(selectedRecipe?.name ?? name),
      };

      const createdBatch = await createBatch(batchToCreate, tenant);

      alert.success(`Batch "${createdBatch.name}" created.`);
      onBatchCreated?.(createdBatch);
      closeModal();
    } catch (error) {
      console.error('Failed to create batch', error);
      alert.error('Failed to create batch. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button variant={buttonVariant} iconName={buttonIconName} onClick={() => setIsVisible(true)} disabled={disabled}>
        {buttonText}
      </Button>

      <Modal
        visible={isVisible}
        header="Create new batch"
        onDismiss={closeModal}
        footer={
          <div style={{ float: 'right' }}>
            <SpaceBetween size="m" direction="horizontal">
              <Button onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCreate} loading={isSaving}>
                Create batch
              </Button>
            </SpaceBetween>
          </div>
        }
      >
        <SpaceBetween size="l" direction="vertical">
          <FormField
            label="Batch name"
            errorText={validate && !formState.name.trim() ? 'Batch name is required' : undefined}
          >
            <Input
              placeholder="Enter batch name"
              value={formState.name}
              onChange={({ detail }) => updateFormField('name', detail.value)}
            />
          </FormField>

          <FormField label="Recipe" errorText={validate && !formState.recipe ? 'Select a recipe' : undefined}>
            <Select
              placeholder={loadingRecipes ? 'Loading recipes' : 'Select recipe'}
              selectedOption={formState.recipe}
              onChange={({ detail }) => updateFormField('recipe', detail.selectedOption ?? null)}
              options={recipeOptions}
              disabled={loadingRecipes || recipeOptions.length === 0}
              filteringType="auto"
              statusType={loadingRecipes ? 'loading' : 'finished'}
              loadingText="Loading recipes"
              empty="No recipes available"
            />
          </FormField>

          <FormField
            label="Start date"
            constraintText="Use YYYY/MM/DD format."
            errorText={validate && !formState.startDate ? 'Start date is required' : undefined}
          >
            <DatePicker
              value={formState.startDate}
              onChange={({ detail }) => updateFormField('startDate', detail.value ?? '')}
            />
          </FormField>

          <FormField
            label="Quantity (L)"
            errorText={validate && Number.isNaN(parseNumber(formState.quantity)) ? 'Enter a valid quantity' : undefined}
          >
            <Input
              type="number"
              value={formState.quantity}
              onChange={({ detail }) => updateFormField('quantity', detail.value)}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
};

export default CreateNewBatchModal;
